import { useEffect, useState } from 'react'

import { Alert } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { GET_DATA_MIGRATIONS } from 'src/graphql/dataMigrations.graphql'
import IconAdd from 'src/icons/IconAdd'
import MigrationsTable from 'src/pages/SettingsPage/tabs/dataMigrationTab/components/MigrationsTable'
import NewMigrationModal from 'src/pages/SettingsPage/tabs/dataMigrationTab/components/NewMigrationModal'
import {
  isPollingPhase,
  MIGRATIONS_POLL_INTERVAL_MS,
} from 'src/pages/SettingsPage/tabs/dataMigrationTab/lib/migrationDisplay'
import { DataMigrationRow } from 'src/pages/SettingsPage/tabs/dataMigrationTab/types'

const DataMigrationTab = () => {
  const [shouldPoll, setShouldPoll] = useState(false)
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false)

  const { data, loading, error, refetch } = useQuery(GET_DATA_MIGRATIONS, {
    fetchPolicy: 'cache-and-network',
    // Bounded polling: only while at least one row is still advancing on its
    // own (-ING phases) or freshly INITIATED and about to be picked up.
    pollInterval: shouldPoll ? MIGRATIONS_POLL_INTERVAL_MS : 0,
  })

  const migrations: DataMigrationRow[] = data?.dataMigrations ?? []

  // Skeletons belong to the FIRST load only. `cache-and-network` re-raises
  // `loading` on every refetch and every poll, so passing it straight through
  // left an empty table looking permanently stuck in a loading state.
  const showSkeletons = loading && !data

  const nextShouldPoll = migrations.some((m) => isPollingPhase(m.phase))
  useEffect(() => {
    setShouldPoll(nextShouldPoll)
  }, [nextShouldPoll])

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto">
      <div className="flex flex-shrink-0 flex-row items-start justify-between gap-4">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Data Migration
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            Import applicants from a previous ATS provider and monitor migration
            progress
          </div>
        </div>
        <DNButton variant="primary" onClick={openModal}>
          <IconAdd className="h-3.5 w-3.5 fill-none stroke-current" />
          Add Migration
        </DNButton>
      </div>

      <NewMigrationModal
        opened={modalOpened}
        onClose={closeModal}
        onCreated={() => refetch()}
      />

      <div className="min-h-0 flex-1">
        {error && migrations.length === 0 ? (
          <Alert color="red" variant="light" className="text-sm">
            <div className="flex flex-row items-center justify-between gap-3">
              <span className="text-doubleNickel-gray-700">
                Couldn&apos;t load migrations.
              </span>
              <DNButton variant="secondary" onClick={() => refetch()}>
                Try again
              </DNButton>
            </div>
          </Alert>
        ) : (
          <MigrationsTable
            migrations={migrations}
            loading={showSkeletons}
            onCancelled={() => refetch()}
          />
        )}
      </div>
    </div>
  )
}

export default DataMigrationTab
